import React, { useState } from "react";
import "./Products.css";

// ===============================
// PRODUCT BROCHURE IMAGES
// Use public/products only.
// Do not import brochure images from src/assets.
// ===============================
const assetVersion = "v=20260622-2";

const productImages = {
  bizstack: `/products/bizstack_brochure.jpeg?${assetVersion}`,
  digitalSignage: `/products/brochure-cms-1.jpeg?${assetVersion}`,
  noc: `/products/brochure_nos.jpeg?${assetVersion}`,
};

// ===============================
// PRODUCTS DATA
// ===============================
const products = [
  {
    name: "BizStack",
    images: [productImages.bizstack],
    description:
      "Business management platform for quotations, invoices, payroll, and operations.",
    features: [
      "Branded invoices & quotations",
      "Sales & cashflow tracking",
      "Automated PDF documents",
      "Claims & expense management",
      "Designed for Singapore SMEs",
    ],
  },
  {
    name: "GridSync Digital Signage",
    images: [productImages.digitalSignage],
    description:
      "Centralised digital signage management system for real-time monitoring, scheduling, and content control.",
    features: [
      "Real-time display monitoring",
      "Scheduled content playback",
      "Centralised signage management",
      "Content designer & layout control",
      "Custom system integrations",
    ],
  },
  {
    name: "Pryce NOC Software",
    images: [productImages.noc],
    description:
      "Network Operations Centre platform for monitoring devices, bandwidth usage, alerts, and panel health in real time.",
    features: [
      "Real-time device monitoring",
      "Bandwidth usage analytics",
      "Device onboarding and registration keys",
      "Fleet-wide dashboard overview",
      "Online, warning, and offline status tracking",
      "Alert monitoring and notifications",
      "Historical bandwidth trend reports",
      "Designed for scalable NOC operations",
    ],
  },
];

export default function Products() {
  const [activeModal, setActiveModal] = useState(null);
  const [imageIndex, setImageIndex] = useState({});
  const [imageOrientation, setImageOrientation] = useState("portrait");

  const whatsappNumber = "6589898221";
  const email = "dominic@prycetech.sg";

  const openModal = (index) => {
    setActiveModal(index);
    setImageOrientation("portrait");
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  const nextImage = () => {
    if (activeModal === null) return;

    setImageIndex((prev) => ({
      ...prev,
      [activeModal]:
        ((prev[activeModal] || 0) + 1) % products[activeModal].images.length,
    }));
  };

  const prevImage = () => {
    if (activeModal === null) return;

    setImageIndex((prev) => ({
      ...prev,
      [activeModal]:
        ((prev[activeModal] || 0) - 1 + products[activeModal].images.length) %
        products[activeModal].images.length,
    }));
  };

  return (
    <div className="products-page">
      <div className="products-container">
        <div className="products-header">
          <span className="products-eyebrow">Our platforms</span>

          <h1 className="products-title">
            Purpose-built products for <span>real-world operations</span>
          </h1>

          <p className="products-intro">
            Pryce Technologies develops practical platforms designed for
            business efficiency, operational visibility, and scalable digital
            workflows across admin systems, finance processes, signage
            management, and network operations monitoring.
          </p>
        </div>

        <div className="products-grid">
          {products.map((product, index) => {
            const currentImageIndex = imageIndex[index] || 0;
            const currentImage = product.images[currentImageIndex];

            return (
              <div key={product.name} className="product-card">
                <div className="product-image" onClick={() => openModal(index)}>
                  <img src={currentImage} alt={product.name} />

                  {product.images.length > 1 && (
                    <div className="image-indicator">
                      {product.images.map((_, i) => (
                        <span
                          key={i}
                          className={
                            currentImageIndex === i ? "dot active" : "dot"
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div className="product-content">
                  <h3>{product.name}</h3>

                  <p>{product.description}</p>

                  <ul className="feature-preview">
                    {product.features.slice(0, 3).map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    className="enquire-btn"
                    onClick={() => openModal(index)}
                  >
                    View details
                  </button>

                  <div className="enquire-options">
                    <a
                      href={`mailto:${email}?subject=Enquiry - ${encodeURIComponent(
                        product.name
                      )}`}
                    >
                      Email
                    </a>

                    <a
                      href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
                        `Hi, I would like to enquire about ${product.name}.`
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {activeModal !== null && (
        <div className="modal-overlay" onClick={closeModal}>
          <div
            className={`modal-content ${imageOrientation}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-image">
              <img
                src={products[activeModal].images[imageIndex[activeModal] || 0]}
                alt={products[activeModal].name}
                onLoad={(e) => {
                  const { naturalWidth, naturalHeight } = e.currentTarget;
                  setImageOrientation(
                    naturalWidth > naturalHeight ? "landscape" : "portrait"
                  );
                }}
              />

              {products[activeModal].images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="nav-arrow left"
                    onClick={prevImage}
                  >
                    ‹
                  </button>

                  <button
                    type="button"
                    className="nav-arrow right"
                    onClick={nextImage}
                  >
                    ›
                  </button>

                  <div className="modal-dots">
                    {products[activeModal].images.map((_, i) => (
                      <span
                        key={i}
                        className={
                          (imageIndex[activeModal] || 0) === i
                            ? "dot active"
                            : "dot"
                        }
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="modal-details">
              <span className="modal-eyebrow">Product overview</span>

              <h2>{products[activeModal].name}</h2>

              <p>{products[activeModal].description}</p>

              <ul className="feature-list">
                {products[activeModal].features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>

              <div className="modal-actions">
                <a
                  className="modal-primary"
                  href={`mailto:${email}?subject=Enquiry - ${encodeURIComponent(
                    products[activeModal].name
                  )}`}
                >
                  Email
                </a>

                <a
                  className="modal-secondary"
                  href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
                    `Hi, I would like to enquire about ${products[activeModal].name}.`
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  WhatsApp
                </a>
              </div>
            </div>

            <button type="button" className="modal-close" onClick={closeModal}>
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
